import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { isFiscalBenefitActiveForPeriod } from "@fiscalo/shared-utils";
import {
  AlertTriangle,
  ChevronDown,
  Download,
  Loader2,
} from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import { Form2181EnvioPanel } from "@/pages/platform-fiscal-benefit/form-2181-envio-panel";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  getFiscalBenefitConfig,
  getFiscalBenefitCreditImpact,
  getForm2181Export,
  getForm2181PadiTxt,
  getForm2181Presentacion,
  runFiscalBenefit,
  setFiscalBenefitCredit,
  setFiscalBenefitDecreto,
  setFiscalBenefitWindowEnd,
  type FiscalBenefitRunSummary,
  type Form2181ExportRow,
} from "@/services/fiscalBenefitAdminService";

/** Previous month in YYYY-MM — the period the monthly cron grants. */
function priorMonth(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Selectable periods, newest first: the last 24 months.
 *
 * A free-text AAAA-MM box let an operator type a malformed or far-future period
 * and get an empty table back, with nothing to distinguish "typo" from "nothing
 * was granted", so the list is clamped to the benefit's own window.
 *
 * `window` is OPTIONAL and defaults to the compiled shared-utils window — but
 * the caller should pass the fetched config's window whenever it has one.
 * Root can now move `window.lastPeriod` from this very page's config section;
 * if this list keeps using the compiled default, extending the vigencia would
 * have no visible effect here at all — the exact "two copies silently
 * disagree" failure the config work exists to eliminate, just moved from a
 * hardcoded constant to a hardcoded DEFAULT parameter instead of being fixed.
 */
function recentPeriods(
  window?: Parameters<typeof isFiscalBenefitActiveForPeriod>[1],
  count = 24,
): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    // padStart is load-bearing: the window comparison is lexicographic, so an
    // unpadded month would sort outside the window.
    const p = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!isFiscalBenefitActiveForPeriod(p, window)) continue;
    out.push(p);
  }
  return out;
}

/**
 * Shared between the config section and the page's period list, so both read
 * the SAME cached query rather than two independently-fetched copies that can
 * disagree about whether the window was just extended.
 */
const FISCAL_BENEFIT_CONFIG_QUERY_KEY = ["platform", "fiscal-benefit", "config"];

const MONTH_NAMES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** What each emitted CFE type is called on screen — "182" alone means nothing. */
const CFE_TYPE_LABEL: Record<string, string> = {
  "111": "e-Factura (111)",
  "182": "e-Resguardo (182)",
};

/** "2026-07" -> "julio 2026" — the period is read by a human, not parsed. */
function periodLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return period;
  return `${MONTH_NAMES_ES[m - 1]} ${y}`;
}

function pesos(minor: number): string {
  return (minor / 100).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function LineTable({
  title, subtitle, rows,
}: {
  title: string;
  subtitle: string;
  rows: Form2181ExportRow[];
}): JSX.Element {
  const total = rows.reduce((a, r) => a + r.creditMinor, 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-foreground">{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sin créditos otorgados en este período — simulá y otorgá para
            emitirlos.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contribuyente</TableHead>
                <TableHead>RUC</TableHead>
                <TableHead className="text-right">Crédito</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.companyId}-${r.ruc ?? "sin-ruc"}`}>
                  <TableCell className="text-sm">
                    {r.legalName ?? (
                      <span className="text-muted-foreground">
                        (sin razón social)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.ruc ?? (
                      // A row with no RUC is excluded from the .txt — DGI
                      // validates the informado RUT — so flag it here rather
                      // than let the file silently come up a line short.
                      <span className="text-destructive">
                        sin RUC — no se declara
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pesos(r.creditMinor)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="font-medium">
                  Total · {rows.length}{" "}
                  {rows.length === 1 ? "contribuyente" : "contribuyentes"}
                </TableCell>
                <TableCell />
                <TableCell className="text-right font-medium tabular-nums">
                  {pesos(total)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Root-only editor for the benefit's window end, decreto and per-year credit.
 *
 * The three fields are edited TOGETHER on purpose, not as three separate
 * settings: extending the vigencia into a year with no credit loaded would
 * silently apply the previous year's figure to every CFE, e-Resguardo and
 * Formulario 2181 line, because the read path is deliberately lenient. The
 * backend refuses that combination; this form just cannot express it
 * one field at a time without the other already being right.
 *
 * Collapsed by default. Edited maybe once a year, when the decreto is
 * extended — it should not push the everyday período/otorgar workflow below
 * the fold every time root opens this page.
 */
function FiscalBenefitConfigSection(): JSX.Element {
  const queryClient = useQueryClient();
  const configQueryKey = FISCAL_BENEFIT_CONFIG_QUERY_KEY;

  const configQuery = useQuery({
    queryKey: configQueryKey,
    queryFn: getFiscalBenefitConfig,
  });

  const invalidateConfig = (): void => {
    void queryClient.invalidateQueries({ queryKey: configQueryKey });
  };

  const errorMessage = (err: unknown, fallback: string): string =>
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback;

  // --- window end ---
  const currentWindowEnd = configQuery.data?.window.lastPeriod;
  const [windowEndInput, setWindowEndInput] = useState("");
  useEffect(() => {
    if (currentWindowEnd !== undefined) setWindowEndInput(currentWindowEnd);
  }, [currentWindowEnd]);

  const saveWindowEnd = useMutation({
    mutationFn: (lastPeriod: string) => setFiscalBenefitWindowEnd(lastPeriod),
    onSuccess: () => {
      toast.success("Vigencia actualizada.");
      invalidateConfig();
    },
    onError: (err) =>
      toast.error(errorMessage(err, "No pudimos actualizar la vigencia."), {
        duration: 10000,
      }),
  });

  // --- decreto ---
  const currentDecreto = configQuery.data?.decreto;
  const [decretoInput, setDecretoInput] = useState("");
  useEffect(() => {
    if (currentDecreto !== undefined) setDecretoInput(currentDecreto);
  }, [currentDecreto]);

  const saveDecreto = useMutation({
    mutationFn: (decreto: string) => setFiscalBenefitDecreto(decreto),
    onSuccess: () => {
      toast.success("Decreto actualizado.");
      invalidateConfig();
    },
    onError: (err) =>
      toast.error(errorMessage(err, "No pudimos actualizar el decreto."), {
        duration: 10000,
      }),
  });

  // --- credit for a year: impact preview, then confirm ---
  const [creditYearInput, setCreditYearInput] = useState("");
  const [creditAmountInput, setCreditAmountInput] = useState("");
  const [pendingImpact, setPendingImpact] = useState<{
    year: number;
    amountMajor: number;
    literalETenantCount: number;
    distinctPlanCount: number;
    appliesToCurrentPricing: boolean;
  } | null>(null);

  const previewCredit = useMutation({
    mutationFn: async () => {
      const year = Number(creditYearInput);
      const amountMajor = Number(creditAmountInput);
      if (!Number.isInteger(year)) {
        throw new Error("El año debe ser un número entero.");
      }
      if (!Number.isFinite(amountMajor) || amountMajor <= 0) {
        throw new Error("El importe debe ser un número positivo.");
      }
      const impact = await getFiscalBenefitCreditImpact(year);
      return {
        year,
        amountMajor,
        literalETenantCount: impact.literalETenantCount,
        distinctPlanCount: impact.distinctPlanCount,
        appliesToCurrentPricing: impact.appliesToCurrentPricing,
      };
    },
    onSuccess: (result) => setPendingImpact(result),
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : "No pudimos calcular el impacto.",
      ),
  });

  const saveCredit = useMutation({
    mutationFn: () => {
      if (!pendingImpact) throw new Error("Nada para guardar.");
      return setFiscalBenefitCredit(pendingImpact.year, pendingImpact.amountMajor);
    },
    onSuccess: () => {
      toast.success("Importe guardado.");
      setPendingImpact(null);
      setCreditYearInput("");
      setCreditAmountInput("");
      invalidateConfig();
    },
    onError: (err) =>
      toast.error(errorMessage(err, "No pudimos guardar el importe."), {
        duration: 10000,
      }),
  });

  const config = configQuery.data;
  const years = config
    ? Object.keys(config.creditByYear)
        .map(Number)
        .sort((a, b) => b - a)
    : [];

  return (
    <Collapsible
      defaultOpen={false}
      className="group rounded-lg border bg-muted/30"
    >
      <CollapsibleTrigger
        type="button"
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-lg p-4 text-left",
          "transition-colors duration-150 hover:bg-muted/40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        )}
      >
        <span className="text-sm font-medium text-foreground">
          Configuración del beneficio — vigencia, decreto e importe anual
        </span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-6 border-t border-border p-4">
          <p className="text-xs text-muted-foreground">
            Estos tres valores rigen todo el cálculo del beneficio, por eso se
            editan juntos: extender la vigencia a un año sin cargar su importe
            aplicaría en silencio el importe del año anterior a cada
            comprobante, e-Resguardo y línea del Formulario 2181. El sistema
            rechaza esa combinación.
          </p>

          {configQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : config ? (
            <>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted-foreground">Vigencia</dt>
                  <dd className="text-sm font-medium text-foreground">
                    {config.window.firstPeriod} → {config.window.lastPeriod}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Decreto vigente
                  </dt>
                  <dd className="text-sm font-medium text-foreground">
                    {config.decreto}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Importe 80 UI por año
                  </dt>
                  <dd className="text-sm font-medium text-foreground">
                    {years
                      .map((y) => `${y}: ${pesos(config.creditByYear[y]!)}`)
                      .join(" · ")}
                  </dd>
                </div>
              </dl>

              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="benefit-window-end">
                    Fin de vigencia (AAAA-MM)
                  </Label>
                  <Input
                    id="benefit-window-end"
                    value={windowEndInput}
                    onChange={(e) => setWindowEndInput(e.target.value)}
                    placeholder="2026-12"
                    className="w-36"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveWindowEnd.mutate(windowEndInput.trim())}
                  disabled={
                    saveWindowEnd.isPending ||
                    windowEndInput.trim() === config.window.lastPeriod
                  }
                >
                  {saveWindowEnd.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  ) : null}
                  Guardar vigencia
                </Button>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="benefit-decreto">Decreto</Label>
                  <Input
                    id="benefit-decreto"
                    value={decretoInput}
                    onChange={(e) => setDecretoInput(e.target.value)}
                    placeholder="206/019"
                    className="w-36"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveDecreto.mutate(decretoInput.trim())}
                  disabled={
                    saveDecreto.isPending || decretoInput.trim() === config.decreto
                  }
                >
                  {saveDecreto.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  ) : null}
                  Guardar decreto
                </Button>
              </div>

              <div className="space-y-3 rounded-md border border-border p-3">
                <p className="text-xs font-medium text-foreground">
                  Cargar importe de 80 UI de un año
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="benefit-credit-year">Año</Label>
                    <Input
                      id="benefit-credit-year"
                      value={creditYearInput}
                      onChange={(e) => {
                        setCreditYearInput(e.target.value);
                        setPendingImpact(null);
                      }}
                      placeholder="2027"
                      className="w-24"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="benefit-credit-amount">Importe (UYU)</Label>
                    <Input
                      id="benefit-credit-amount"
                      value={creditAmountInput}
                      onChange={(e) => {
                        setCreditAmountInput(e.target.value);
                        setPendingImpact(null);
                      }}
                      placeholder="540"
                      className="w-28"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => previewCredit.mutate()}
                    disabled={
                      previewCredit.isPending ||
                      !creditYearInput ||
                      !creditAmountInput
                    }
                  >
                    {previewCredit.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    ) : null}
                    Ver impacto
                  </Button>
                </div>

                {pendingImpact ? (
                  <div className="space-y-2 rounded-md bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
                    <p className="flex items-start gap-2 text-amber-900 dark:text-amber-200">
                      <AlertTriangle
                        className="mt-0.5 h-4 w-4 shrink-0"
                        aria-hidden
                      />
                      {pendingImpact.appliesToCurrentPricing ? (
                        <>
                          Guardar {pesos(Math.round(pendingImpact.amountMajor * 100))}{" "}
                          UYU para {pendingImpact.year} afecta a{" "}
                          {pendingImpact.literalETenantCount} contribuyente(s)
                          Literal E en {pendingImpact.distinctPlanCount} plan(es)
                          con descuento. Esto NO repricea las suscripciones por sí
                          solo — hace falta correr la revaluación por separado.
                        </>
                      ) : (
                        <>
                          {pendingImpact.year} no es el año que se usa para
                          calcular el precio de las suscripciones activas hoy
                          — eso lo decide el año calendario actual. Guardar
                          este importe no afecta a ningún contribuyente ahora
                          mismo; queda cargado para cuando corresponda.
                        </>
                      )}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => saveCredit.mutate()}
                        disabled={saveCredit.isPending}
                      >
                        {saveCredit.isPending ? (
                          <Loader2
                            className="mr-2 h-4 w-4 animate-spin"
                            aria-hidden
                          />
                        ) : null}
                        Confirmar y guardar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingImpact(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <p className="text-sm text-destructive">
              No pudimos cargar la configuración.
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}


/**
 * Root-only surface for the 80 UI fiscal benefit (Decreto 206/019): inspect a
 * period's granted credits, download the PADI import file, and dry-run a grant.
 *
 * Exists because the accountant has to load Form 2181 into PADI by hand — DGI
 * has no API for it — so someone needs to see what will be filed before filing
 * it.
 */
export function PlatformFiscalBenefitPage(): JSX.Element {
  const [period, setPeriod] = useState(priorMonth());
  // Same query key as FiscalBenefitConfigSection below — react-query shares
  // the cache, so this is not a second network request in the common case.
  // Reading the window here (rather than recentPeriods() defaulting silently)
  // is what makes an extended vigencia actually show up in the dropdown.
  const configQuery = useQuery({
    queryKey: FISCAL_BENEFIT_CONFIG_QUERY_KEY,
    queryFn: getFiscalBenefitConfig,
  });
  const configWindow = configQuery.data?.window;
  const periodOptions = useMemo(
    () => recentPeriods(configWindow),
    [configWindow],
  );

  // Changing the period must disarm: the confirmation names a period, and an
  // armed button that now points at a different month is exactly the misfire
  // this guard exists to prevent.
  useEffect(() => {
    setConfirmRun(false);
    setSummary(null);
    setChainPending(false);
  }, [period]);
  const [summary, setSummary] = useState<FiscalBenefitRunSummary | null>(null);
  /** Two-step arm for the real run. Reset whenever the period changes. */
  const [confirmRun, setConfirmRun] = useState(false);
  /* The CFE types this run needs but the issuer cannot emit. Empty on an empty
     run and whenever every CAE is in place, so the banner stays absent unless
     there is something to act on. */
  const missingCae = (summary?.caePreflight ?? []).filter(
    (p) => !p.hasUsableCae,
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["platform", "fiscal-benefit", "2181", period],
    queryFn: () => getForm2181Export(period),
    retry: false,
  });

  /* Granted rows on the ledger — the one fact that says whether this period was
     already otorgado. It is also what the backend's source=auto keys off, so
     labelling the .txt button from it cannot disagree with the file it returns. */
  const grantedCount =
    (data?.line416.length ?? 0) + (data?.line426.length ?? 0);
  const otorgado = grantedCount > 0;
  /* A simulation only counts for the period it was run against: changing the
     period clears `summary`, but a stale one would otherwise describe a month
     the operator is no longer looking at. */
  const simulated = summary?.dryRun === true && summary.period === period;
  const simulatedCount = simulated
    ? summary.ivaMinimoIssued + summary.generalIssued
    : 0;

  /* Bumped once otorgar lands, to tell the panel to begin the upload. A signal
     rather than a boolean so re-filing the same period after a failed upload
     still triggers a fresh session. */
  const [envioSignal, setEnvioSignal] = useState(0);
  /* True while a simulation was started BY the Subir chain, so its result arms
     the confirm instead of just refreshing the summary card. */
  const [chainPending, setChainPending] = useState(false);

  const download = useMutation({
    mutationFn: () => getForm2181PadiTxt(period),
    onSuccess: ({ txt, rows, centavosDropped, returnedSource }) => {
      if (rows === 0) {
        toast.error(
          `No hay créditos en ${periodLabel(period)}, ni otorgados ni proyectados, así que no hay archivo que generar.`,
          { duration: 8000 },
        );
        return;
      }
      const url = URL.createObjectURL(
        new Blob([txt], { type: "text/plain;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      // A projection and the filed file are different documents with the same
      // shape, so they must not land on disk under the same name.
      a.download = `2181-${period}${returnedSource === "simulation" ? "-proyeccion" : ""}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      if (returnedSource === "simulation") {
        toast.success(
          `Proyección de ${periodLabel(period)} (${rows} línea(s)): todavía no otorgaste este período. Importalo en PADI para validar los RUT y el formato; después otorgá y descargá de nuevo para presentar.`,
          { duration: 12000 },
        );
      }
      // Should never fire: credits are rounded to whole pesos at grant time.
      // If it does, the ledger and the client's CFE/e-Resguardo already
      // disagree with this file — worth stopping for, not a footnote.
      if (centavosDropped > 0) {
        toast.error(
          `Atención: ${pesos(centavosDropped)} en centésimos no se declaran. Revisá cómo se otorgaron los créditos antes de importar.`,
          { duration: 10000 },
        );
      }
    },
    onError: () => toast.error("No pudimos generar el archivo PADI."),
  });

  /**
   * The real grant. Deliberately NOT the same control as the simulation.
   *
   * This issues real CFEs (Literal E) and e-Resguardos 182 (régimen general) to
   * DGI, dated the last day of the period, and writes the granted-credit ledger
   * the PADI export reads. It is idempotent per company+period+línea, so a
   * second run cannot double-issue — but it also cannot be undone, and for a
   * past period the documents it emits are back-dated.
   *
   * So it is gated behind the simulation: you cannot press it until you have
   * looked at who would be credited.
   */
  /**
   * The PRESENTATION file — what gets uploaded to Servicios en linea, so PADI
   * is not needed in the middle of the filing. Deliberately a separate control
   * from the import .txt: they are different documents for different steps,
   * and one button that silently changes meaning is how the wrong file gets
   * filed. Reads the LEDGER only — a projection must never be presentable.
   */
  const downloadPresentacion = useMutation({
    mutationFn: () => getForm2181Presentacion(period),
    onSuccess: ({ txt, filename, crc, rows }) => {
      if (rows === 0) {
        toast.error(
          `No hay créditos otorgados en ${periodLabel(period)}. Otorgá el período antes de generar el archivo para presentar.`,
          { duration: 8000 },
        );
        return;
      }
      const url = URL.createObjectURL(
        new Blob([txt], { type: "text/plain;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      // DGI's own name: the CRC inside the file has to match the one in the
      // filename, so this comes from the server rather than being rebuilt.
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        `Archivo para presentar generado (${rows} línea(s), CRC ${crc}). Subilo en Servicios en línea › Envío de declaraciones.`,
        { duration: 12000 },
      );
    },
    onError: () =>
      toast.error("No pudimos generar el archivo para presentar."),
  });

  const realRun = useMutation({
    mutationFn: () => runFiscalBenefit(period, false),
    onSuccess: (s) => {
      setSummary(s);
      setConfirmRun(false);
      void refetch();
      const issued = s.ivaMinimoIssued + s.generalIssued;
      if (s.errors.length > 0) {
        toast.error(
          `Otorgamiento con ${s.errors.length} error(es). Se emitieron ${issued} documento(s).`,
          { duration: 10000 },
        );
      } else {
        toast.success(`Otorgamiento realizado: ${issued} documento(s) emitidos.`);
      }

    },
    onError: () => toast.error("No pudimos ejecutar el otorgamiento."),
  });

  const dryRun = useMutation({
    mutationFn: () => runFiscalBenefit(period, true),
    onSuccess: (s) => {
      setSummary(s);
      void refetch();
      // Only the chain arms the confirm. A simulation run for inspection must
      // never leave an irreversible button primed behind the operator's back.
      if (chainPending) {
        setChainPending(false);
        const count = s.ivaMinimoIssued + s.generalIssued;
        if (count === 0) {
          toast.error(
            "La simulación de este período no otorga ningún crédito, así que no hay nada que presentar.",
            { duration: 8000 },
          );
          return;
        }
        setConfirmRun(true);
      }
    },
    onError: () => {
      setChainPending(false);
      toast.error("No pudimos simular el otorgamiento.");
    },
  });

  /**
   * The whole filing, in the only order the data allows: simular -> confirmar
   * -> otorgar -> generar -> subir.
   *
   * Upload cannot come first. The presentation file is built from the granted
   * ledger and otorgar is what writes it, so filing before otorgar would mean
   * submitting the PROJECTION — which can name a credit whose CFE then fails to
   * emit, i.e. declaring to DGI something that never happened.
   */
  const startFilingChain = (): void => {
    if (otorgado || simulated) {
      if (!otorgado && simulatedCount === 0) {
        toast.error(
          "La simulación de este período no otorga ningún crédito, así que no hay nada que presentar.",
          { duration: 8000 },
        );
        return;
      }
      setConfirmRun(true);
      return;
    }
    setChainPending(true);
    dryRun.mutate();
  };

  /**
   * Called by the panel once DGI has taken the declaration.
   *
   * This is the ONLY path that emits comprobantes. Presenting is retryable;
   * emitting is not, so the irreversible half runs last and only on a
   * submission we actually observed.
   *
   * Read `outcomeConfirmed` honestly: it means the flow recognised the page's
   * acknowledgement, NOT that DGI accepted the declaration — that verdict is
   * emailed. Emission is therefore gated on DGI having RECEIVED the file, which
   * is the strongest signal available today. It gets stricter for free once
   * SUCCESS_PATTERNS is filled in from a real upload.
   */
  const onEnvioSubmitted = (fromProjection: boolean): void => {
    if (!fromProjection) return; // already otorgado — nothing left to emit
    if (realRun.isPending) return;
    realRun.mutate();
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <PageHeader
        title="Beneficio fiscal 80 UI"
        description="Créditos otorgados (Decreto 206/019) y archivo de importación para PADI."
      />

      <FiscalBenefitConfigSection />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="space-y-1">
            <Label htmlFor="period">Período</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger id="period" className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((p) => (
                  <SelectItem key={p} value={p}>
                    {periodLabel(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Label tracks what the backend's source=auto will actually return.
              One label for two different documents is how the wrong file gets
              filed: before otorgar this is a projection, for validating in PADI
              only, and it is NOT presentable. */}
          <Button
            type="button"
            variant="outline"
            onClick={() => download.mutate()}
            disabled={download.isPending}
          >
            {download.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Download className="mr-2 h-4 w-4" aria-hidden />
            )}
            {otorgado
              ? "Descargar PADI (.txt)"
              : "Descargar proyección (.txt)"}
          </Button>

          {/* One primary action for the whole filing. The chain is
              simular -> confirmar -> otorgar -> generar -> subir, in that
              order, because the presentation file is built from the LEDGER and
              otorgar is what writes it. Uploading first would mean filing the
              projection, and a projection can contain a credit whose CFE then
              fails to emit — a declaration to DGI for something that never
              happened. The confirm step stays: otorgar is irreversible and
              back-dated, so a human names the number of comprobantes once. */}
          {confirmRun ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  setConfirmRun(false);
                  setEnvioSignal((n) => n + 1);
                }}
                disabled={realRun.isPending}
              >
                <AlertTriangle className="mr-2 h-4 w-4" aria-hidden />
                {otorgado
                  ? "Confirmar: presentar en DGI"
                  : `Confirmar: presentar y emitir ${simulatedCount} comprobante(s)`}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmRun(false)}
                disabled={realRun.isPending}
              >
                Cancelar
              </Button>
            </div>
          ) : (
            <Form2181EnvioPanel
              period={period}
              periodLabel={periodLabel(period)}
              startSignal={envioSignal}
              busy={dryRun.isPending || realRun.isPending}
              onRequestStart={startFilingChain}
              onSubmitted={onEnvioSubmitted}
              onDownloadPresentacion={() => downloadPresentacion.mutate()}
            />
          )}
        </CardContent>
      </Card>

      {/* One status line instead of three paragraphs. It names the state and the
          single next step. "Subir a DGI" is one button but several irreversible
          things, so the line always says what pressing it will do next. */}
      <p className="text-xs text-muted-foreground">
        {otorgado
          ? `Otorgado: ${grantedCount} crédito(s) registrados. "Subir a DGI" presenta la declaración en Servicios en línea; el resultado llega por correo a dgi@fiscalo.uy.`
          : !simulated
            ? '"Subir a DGI" hace todo: simula, te pide confirmación, presenta la declaración y — sólo si DGI la recibe — emite los comprobantes. Antes podés descargar la proyección para validarla en PADI.'
            : simulatedCount === 0
              ? "La simulación de este período no otorga ningún crédito, así que no hay nada para emitir."
              : `Simulado: ${simulatedCount} crédito(s). "Subir a DGI" presenta primero y emite después: los comprobantes salen sólo si DGI recibe la declaración. Emitir no se puede deshacer.`}
      </p>

      {/* Credits held back because DGI has not accepted the CFE that delivers
          them. Loud on purpose: a declaration that silently drops rows is worse
          than one that says why, and `pending` in particular means a poll job is
          stuck — a CFE confirms in ~34s and filing happens days later. */}
      {data && (data.withheld.rejected > 0 || data.withheld.pending > 0) ? (
        <p className="flex items-start gap-1.5 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {data.withheld.rejected > 0
              ? `${data.withheld.rejected} crédito(s) no se declaran: DGI rechazó (o se anuló) el comprobante que los respalda. Revisá la emisión antes de presentar. `
              : ""}
            {data.withheld.pending > 0
              ? `${data.withheld.pending} crédito(s) quedan fuera por ahora: DGI todavía no confirmó su comprobante. Normalmente tarda segundos — si sigue así, hay algo trabado.`
              : ""}
          </span>
        </p>
      ) : null}

      {/* Data-loss warning, kept but scoped to when a .txt is actually in play:
          a PADI import replaces the form's entire contents, so importing a
          period twice or importing a partial file silently discards what was
          there. */}
      {simulated || otorgado ? (
        <p className="text-xs text-muted-foreground">
          Al importar en PADI se borran los datos cargados previamente en el
          formulario. Importá el archivo completo del período.
        </p>
      ) : null}

      {summary ? (
        <Card>
          <CardHeader>
            {/* The same card now backs both a projection and a real run, so it
                must say which. Hardcoding "no se emitieron documentos" would be
                a lie after an otorgamiento, and the wrong lie to tell. */}
            <CardTitle className="text-base text-foreground">
              {summary.dryRun ? "Simulación" : "Otorgamiento"} {summary.period}
            </CardTitle>
            <CardDescription>
              {summary.dryRun
                ? "Proyección: no se emitieron documentos ni se registraron créditos."
                : "Documentos emitidos ante DGI y créditos registrados. No se puede deshacer."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {/* A stat row rather than three sentences: these are four figures
                the operator scans, not prose they read. */}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
              {[
                { label: "IVA mínimo (Art. 5)", value: summary.ivaMinimoIssued },
                { label: "Régimen general (Art. 4)", value: summary.generalIssued },
                { label: "Ya otorgados", value: summary.skippedAlreadyGranted },
                { label: "Sin declaración", value: summary.skippedNoDeclaration },
                // Another proveedor habilitado covered these months — the guard
                // that stops two providers declaring the same client-month.
                { label: "Fuera de nuestro período", value: summary.skippedNotOurPeriod },
                { label: "Sin alta como proveedor", value: summary.skippedNotYetOurCliente },
              ].map((stat) => (
                <div key={stat.label}>
                  <dt className="text-xs text-muted-foreground">{stat.label}</dt>
                  <dd className="text-xl font-semibold tabular-nums text-foreground">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
            {summary.skippedNoDeclaration > 0 ? (
              <p className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                Les corresponde el crédito en cuanto firmen la declaración.
              </p>
            ) : null}
            {/* The pre-flight. Without it the operator finds out a CFE type has
                no CAE one tenant at a time, in the errors list, AFTER having
                downloaded and validated a PADI file — which is exactly what
                happened on 2026-08-27 with the missing 182. Said up front,
                while Otorgar has not been pressed yet.

                Deliberately NOT a hard gate on the button: the run is idempotent
                per company+período+línea, so issuing the types that DO have a
                CAE now and the rest after loading the missing one is a
                legitimate workflow. Blocking it would remove a valid choice to
                prevent a failure the operator has just been told about. */}
            {missingCae.length > 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
                <p className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  {summary.dryRun
                    ? "Esta corrida no va a poder completarse"
                    : "Faltaba CAE al momento de otorgar"}
                </p>
                <ul className="mt-2 space-y-1 text-amber-800 dark:text-amber-300">
                  {missingCae.map((p) => (
                    <li key={p.cfeType}>
                      {p.rowCount}{" "}
                      {p.rowCount === 1 ? "comprobante" : "comprobantes"}{" "}
                      {CFE_TYPE_LABEL[p.cfeType] ?? `CFE ${p.cfeType}`} (línea{" "}
                      {p.formLine}): el emisor no tiene CAE {p.cfeType} vigente y
                      disponible.
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  Cargá el CAE que falta antes de otorgar. Si otorgás igual, esos
                  comprobantes fallan uno por uno y el resto sí se emite; podés
                  volver a otorgar después para completarlos.
                </p>
              </div>
            ) : null}
            {summary.errors.length > 0 ? (
              // Listed, not counted. "Errores: 5" tells the operator something
              // is wrong but not what or for whom, which is the same as not
              // telling them: the run is per-tenant, so the only actionable
              // form is which tenant failed and why.
              <div className="space-y-1 pt-1">
                <p className="flex items-center gap-2 font-medium text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  {summary.errors.length}{" "}
                  {summary.errors.length === 1
                    ? "contribuyente falló"
                    : "contribuyentes fallaron"}
                </p>
                <ul className="space-y-1">
                  {summary.errors.map((e) => (
                    <li
                      key={`${e.companyId}-${e.error}`}
                      className="rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs"
                    >
                      <span className="font-medium">
                        {e.legalName ?? "(sin razón social)"}
                      </span>
                      {e.rut ? (
                        <span className="ml-2 font-mono text-muted-foreground">
                          {e.rut}
                        </span>
                      ) : null}
                      <span className="mt-1 block text-destructive">
                        {e.error}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : data ? (
        <>
          {/* The number actually being declared to DGI, above the detail rather
              than trailing after it as muted body text. It is also the figure
              the operator reconciles against PADI after importing, so it leads. */}
          {/* The single most confusing thing on this page: the tables below are
              the LEDGER (credits actually issued), while the simulation card is
              a PROJECTION. They routinely disagree — a period that has never
              been otorgado shows an empty table while the simulation lists ten
              tenants — and nothing said so. */}
          <p className="text-xs text-muted-foreground">
            Lo de abajo son los créditos <strong>ya otorgados</strong> — lo que
            va a salir en el archivo PADI. La simulación, en cambio, muestra lo
            que se otorgaría si corrieras el período ahora. Si no coinciden, es
            porque todavía no otorgaste este período.
          </p>

          <Card>
            <CardContent className="flex flex-wrap items-end justify-between gap-4 pt-6">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Total del período · {periodLabel(period)}
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                  {pesos(data.totalMinor)}
                  <span className="ml-2 text-base font-normal text-muted-foreground">
                    UYU
                  </span>
                </p>
              </div>
              <dl className="flex gap-6 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Línea 426</dt>
                  <dd className="tabular-nums text-foreground">
                    {pesos(
                      data.line426.reduce((a, r) => a + r.creditMinor, 0),
                    )}
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({data.line426.length})
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Línea 416</dt>
                  <dd className="tabular-nums text-foreground">
                    {pesos(
                      data.line416.reduce((a, r) => a + r.creditMinor, 0),
                    )}
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({data.line416.length})
                    </span>
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <LineTable
            title="Línea 426 — IVA mínimo (Literal E) · ya otorgados"
            subtitle="Art. 5: Fiscalo descontó el crédito del precio y lo recupera en su propia declaración."
            rows={data.line426}
          />
          <LineTable
            title="Línea 416 — Régimen general · ya otorgados"
            subtitle="Art. 4: el cliente computa el crédito en su propia declaración; Fiscalo no recupera nada."
            rows={data.line416}
          />
        </>
      ) : null}
    </div>
  );
}
